/**
 * Pure helpers for the 6-digit PWA login flow. Kept free of React and
 * network so they are unit-testable (repo convention: pure-function tests).
 */

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CODE_REGEX = /^\d{6}$/;

export function isValidLoginCode(code: string): boolean {
  return CODE_REGEX.test(code);
}

export function normalizeLoginEmail(raw: string): string | null {
  const normalized = raw.trim().toLowerCase();
  return EMAIL_REGEX.test(normalized) ? normalized : null;
}

export interface LoginWorkspace {
  clientId: string;
  clientName: string;
  role?: string;
}

/**
 * Distinct roles across the pickable workspaces, client first (DD-026).
 * More than one distinct role means the login flow asks "which role"
 * before "which client". Missing roles count as client.
 */
export function distinctLoginRoles(workspaces: LoginWorkspace[]): string[] {
  const seen = new Set<string>();
  const roles: string[] = [];
  for (const workspace of workspaces) {
    const role = workspace.role || "client";
    if (!seen.has(role)) {
      seen.add(role);
      roles.push(role);
    }
  }
  roles.sort((a, b) => {
    if (a === "client") return -1;
    if (b === "client") return 1;
    return a.localeCompare(b);
  });
  return roles;
}

/** Workspaces matching a chosen role (missing roles count as client). */
export function workspacesForRole(
  workspaces: LoginWorkspace[],
  role: string
): LoginWorkspace[] {
  return workspaces.filter((w) => (w.role || "client") === role);
}

/**
 * True when running as an installed PWA (home-screen launch). The code
 * login is the default path there because emailed magic links open in
 * the browser's storage partition, not the installed app's.
 */
export function isStandaloneDisplayMode(): boolean {
  if (typeof window === "undefined") return false;
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches === true ||
    nav.standalone === true
  );
}
